import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { generateOrganizationSchema } from '@/utils/schemaMarkup';

export interface SEOSettings {
	site_title: string;
	site_description: string | null;
	default_meta_title_template: string | null;
	default_meta_description_template: string | null;
	google_analytics_id: string | null;
	google_search_console_id: string | null;
	bing_webmaster_id: string | null;
	facebook_app_id: string | null;
	facebook_url: string | null;
	canonical_domain: string | null;
	robots_txt: string | null;
	sitemap_enabled: boolean | null;
	schema_org_enabled: boolean | null;
	open_graph_enabled: boolean | null;
	ga4_property_url?: string | null;
	gsc_property_url?: string | null;
	looker_studio_url?: string | null;
}

interface SEOContextValue {
	settings: SEOSettings | null;
	isLoading: boolean;
	refresh: () => Promise<void>;
}

const SEOContext = createContext<SEOContextValue>({ settings: null, isLoading: true, refresh: async () => {} });

const ensureMetaTag = (key: { name?: string; property?: string }, content: string) => {
	const selector = key.property ? `meta[property="${key.property}"]` : `meta[name="${key.name}"]`;
	let el = document.querySelector(selector) as HTMLMetaElement | null;
	if (!el) {
		el = document.createElement('meta');
		if (key.property) el.setAttribute('property', key.property);
		if (key.name) el.setAttribute('name', key.name);
		document.head.appendChild(el);
	}
	el.setAttribute('content', content);
};

const ensureJsonLd = (id: string, data: object) => {
	let el = document.getElementById(id) as HTMLScriptElement | null;
	if (!el) {
		el = document.createElement('script');
		el.type = 'application/ld+json';
		el.id = id;
		document.head.appendChild(el);
	}
	el.textContent = JSON.stringify(data);
};

const injectGA4 = (gaId: string) => {
	if (!gaId) return;
	// gtag base script
	if (!document.querySelector(`script[src*="https://www.googletagmanager.com/gtag/js?id=${gaId}"]`)) {
		const gtagScript = document.createElement('script');
		gtagScript.async = true;
		gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
		document.head.appendChild(gtagScript);
	}
	// gtag config script
	if (!document.getElementById('gtag-init')) {
		const inline = document.createElement('script');
		inline.id = 'gtag-init';
		inline.innerHTML = `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', '${gaId}');`;
		document.head.appendChild(inline);
	}
};

export const SEOProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [settings, setSettings] = useState<SEOSettings | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	const loadSettings = async () => {
		setIsLoading(true);
		try {
			const { data, error } = await supabase.from('seo_settings').select('*').limit(1);
			if (!error && data && data.length > 0) {
				setSettings(data[0] as unknown as SEOSettings);
			}
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		loadSettings();
	}, []);

	useEffect(() => {
		if (!settings) return;
		const baseUrl = settings.canonical_domain || 'https://floridahighwaymenauction.com';
		// Verification meta tags
		if (settings.google_search_console_id) {
			ensureMetaTag({ name: 'google-site-verification' }, settings.google_search_console_id);
		}
		if (settings.bing_webmaster_id) {
			ensureMetaTag({ name: 'msvalidate.01' }, settings.bing_webmaster_id);
		}
		// Social defaults - Facebook only
		if (settings.site_title) {
			ensureMetaTag({ property: 'og:site_name' }, settings.site_title);
		}
		if (settings.facebook_app_id) {
			ensureMetaTag({ property: 'fb:app_id' }, settings.facebook_app_id);
		}
		// GA4
		if (settings.google_analytics_id) {
			injectGA4(settings.google_analytics_id);
		}
		// WebSite + SearchAction
		if (settings.schema_org_enabled !== false) {
			ensureJsonLd('ld-website', {
				"@context": "https://schema.org",
				"@type": "WebSite",
				"name": settings.site_title || 'Florida Highwaymen Auction',
				"url": baseUrl,
				"potentialAction": {
					"@type": "SearchAction",
					"target": `${baseUrl}/artworks?query={search_term_string}`,
					"query-input": "required name=search_term_string"
				}
			});

			// Organization schema with publishing principles
			const org: any = generateOrganizationSchema();
			org.publishingPrinciples = `${baseUrl}/editorial-policy`;
			ensureJsonLd('ld-organization', org);
		}
	}, [settings]);

	const value = useMemo<SEOContextValue>(() => ({ settings, isLoading, refresh: loadSettings }), [settings, isLoading]);

	return (
		<SEOContext.Provider value={value}>
			{children}
		</SEOContext.Provider>
	);
};

export const useSEOSettings = () => useContext(SEOContext);
